import {
  AnySchema,
  array,
  boolean,
  date,
  number,
  object,
  ObjectSchema,
  string
} from "yup";
import { ETipoVisualizacao } from "../../../../@types/IntegracaoCampoEnum";
import { TIntegracaoCampoModel } from "../../../../@types/IntegracaoCampoModel";
import useNamedStringValidators from "../useNamedStringValidators/useNamedStringValidators";
import useFieldLayout from "../useFieldLayout/useFieldLayout";
import useDisplayRule from "../useDisplayRule/useDisplayRule";

export default function useBuildValidationSchema() {
  const { valueMatchesDisplayRule } = useDisplayRule();
  const { layoutType } = useFieldLayout();
  const { applyNamedValidation } = useNamedStringValidators();

  const fieldLabel = (field: TIntegracaoCampoModel): string =>
    (field.label ?? "").replace(/:\s*$/, "").trim() || "Campo";

  const fieldShouldBeRequired = (
    field: TIntegracaoCampoModel,
    values: Record<string, unknown>
  ): boolean => {
    if (field.regraObrigatoriedade) {
      return valueMatchesDisplayRule(field.regraObrigatoriedade, values);
    }

    return field.obrigatorio;
  };

  const fieldIsVisibleForValidation = (
    field: TIntegracaoCampoModel,
    values: Record<string, unknown>
  ): boolean => {
    if (!field.regraExibicao) return true;

    return valueMatchesDisplayRule(field.regraExibicao, values);
  };

  const validateRequiredWhenVisible = (
    field: TIntegracaoCampoModel,
    values: Record<string, unknown>,
    validate: () => boolean
  ): boolean => {
    if (!fieldShouldBeRequired(field, values)) return true;
    if (!fieldIsVisibleForValidation(field, values)) return true;

    return validate();
  };

  const stringSchema = (field: TIntegracaoCampoModel) => {
    let schema = string().nullable();
    schema = applyNamedValidation(
      schema as any,
      field.validacaoNomeada
    ) as typeof schema;
    if (field.tamanho)
      schema = schema.max(field.tamanho, `Máximo ${field.tamanho} caracteres`);

    if (field.minLength) {
      const minLength = field.minLength;
      const message =
        minLength === field.tamanho
          ? `Deve ter exatamente ${minLength} caracteres`
          : `Mínimo ${minLength} caracteres`;

      schema = schema.test(
        "min-length",
        message,
        (value) => !value || String(value).length >= minLength
      );
    }

    schema = schema.test(
      "required-when-visible",
      `${fieldLabel(field)} é obrigatório`,
      function (value) {
        return validateRequiredWhenVisible(
          field,
          this.parent as Record<string, unknown>,
          () => Boolean(value)
        );
      }
    );

    return schema;
  };

  const stringArraySchema = (field: TIntegracaoCampoModel): AnySchema => {
    let schema = array()
      .of(string())
      .nullable()
      .typeError(`${fieldLabel(field)} inválido`);

    schema = schema.test(
      "required-when-visible",
      `${fieldLabel(field)} é obrigatório`,
      function (value) {
        return validateRequiredWhenVisible(
          field,
          this.parent as Record<string, unknown>,
          () => Array.isArray(value) && value.length > 0
        );
      }
    ) as typeof schema;

    return schema;
  };

  const numberArraySchema = (field: TIntegracaoCampoModel): AnySchema => {
    let schema = array()
      .of(
        string().matches(
          /^\d+$/,
          `${fieldLabel(field)} deve conter apenas números`
        )
      )
      .nullable()
      .typeError(`${fieldLabel(field)} inválido`);

    schema = schema.test(
      "required-when-visible",
      `${fieldLabel(field)} é obrigatório`,
      function (value) {
        return validateRequiredWhenVisible(
          field,
          this.parent as Record<string, unknown>,
          () => Array.isArray(value) && value.length > 0
        );
      }
    ) as typeof schema;

    return schema;
  };

  const remoteCollectionSchema = (field: TIntegracaoCampoModel): AnySchema => {
    const checkedKey = field.remoteCollection?.CheckedKey ?? "Checked";

    let schema = array()
      .nullable()
      .typeError(`${fieldLabel(field)} inválido`);

    schema = schema.test(
      "required-when-visible",
      `Selecione ao menos uma opção`,
      function (value) {
        return validateRequiredWhenVisible(
          field,
          this.parent as Record<string, unknown>,
          () =>
            Array.isArray(value) &&
            value.some((row) =>
              Boolean((row as Record<string, unknown> | null)?.[checkedKey])
            )
        );
      }
    ) as typeof schema;

    return schema;
  };

  const numberSchema = (field: TIntegracaoCampoModel): AnySchema => {
    let schema = number()
      .nullable()
      .typeError(`${fieldLabel(field)} inválido`);

    schema = schema.test(
      "required-when-visible",
      `${fieldLabel(field)} é obrigatório`,
      function (value) {
        return validateRequiredWhenVisible(
          field,
          this.parent as Record<string, unknown>,
          () => value !== null && value !== undefined
        );
      }
    );

    return schema;
  };

  const booleanSchema = (field: TIntegracaoCampoModel): AnySchema => {
    let schema = boolean();
    const requiredMessage = field.regraObrigatoriedade
      ? "Selecione ao menos uma opção"
      : `${fieldLabel(field)} é obrigatório`;

    schema = schema.test(
      "required-when-visible",
      requiredMessage,
      function (value) {
        return validateRequiredWhenVisible(
          field,
          this.parent as Record<string, unknown>,
          () => value === true
        );
      }
    );

    return schema;
  };

  const dateSchema = (field: TIntegracaoCampoModel): AnySchema => {
    let schema = date().nullable();

    schema = schema.test(
      "required-when-visible",
      `${fieldLabel(field)} é obrigatório`,
      function (value) {
        return validateRequiredWhenVisible(
          field,
          this.parent as Record<string, unknown>,
          () => value != null
        );
      }
    );

    return schema;
  };

  const schemaByLayout = (field: TIntegracaoCampoModel): AnySchema => {
    switch (layoutType(field)) {
      case ETipoVisualizacao.NUMBER:
        return numberSchema(field);
      case ETipoVisualizacao.INPUT_TAG_NUMBER:
        return numberArraySchema(field);
      case ETipoVisualizacao.DATE:
        return dateSchema(field);
      case ETipoVisualizacao.CHECKBOX:
        return booleanSchema(field);
      case ETipoVisualizacao.REMOTE_COLLECTION:
        return remoteCollectionSchema(field);
      case ETipoVisualizacao.SELECT_MULTIPLE:
      case ETipoVisualizacao.UPLOAD:
      case ETipoVisualizacao.INPUT_TAG_TEXT:
      case ETipoVisualizacao.CHECKBOX_GROUP:
        return stringArraySchema(field);
      case ETipoVisualizacao.TEXT:
      case ETipoVisualizacao.SELECT:
      case ETipoVisualizacao.PASSWORD:
      case ETipoVisualizacao.RADIO:
      case ETipoVisualizacao.BUTTON:
      default:
        return stringSchema(field);
    }
  };

  const buildSchema = (fields: TIntegracaoCampoModel[]): ObjectSchema<any> => {
    const shape = fields.reduce<Record<string, AnySchema>>((acc, field) => {
      acc[field.nomeCampo] = schemaByLayout(field);

      return acc;
    }, {});

    return object().shape(shape);
  };

  return {
    buildSchema
  };
}
